import { ProgramPage } from '@/features/digitalPrograms/ProgramPage'
import { ProgramAdminPage } from '@/features/digitalPrograms/ProgramAdminPage'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'

function LegacyProjectsRedirect() {
  const { pathname, search, hash } = useLocation()
  const target = pathname.replace(/^\/admin\/projects/, '/admin/client-services') + search + hash
  return <Navigate to={target} replace />
}

// Layouts & Guards
import { PublicLayout } from '@/components/layout/PublicLayout'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AdminRoute } from '@/components/common/AdminRoute'

// Auth
import { SignInPage } from '@/features/auth'

// Public pages
import { HomePage } from '@/features/public'
import { SeasonsPage, FilmDetailPage, ProjectsPage } from '@/features/seasons'
import { CompanyPage, MemberPage } from '@/features/company'
import { ServicesPage } from '@/features/services'
import { RequestPage } from '@/features/request'
import { ApplicationsPage, ApplicationDetailPage } from '@/features/applications'
import { ProductionConfirmPage } from '@/features/productions'
import { FestivalsPage, FestivalPage } from '@/features/festivals'
import { ConnectPage } from '@/features/mailing-list'

// Legal pages
import { TermsOfServicePage, PrivacyPolicyPage } from '@/features/legal'

// Admin pages
import {
  AdminApplicationsPage,
  AdminApplicationEditPage,
  AdminFormBuilderPage,
  AdminFieldLibraryPage,
  AdminSectionLibraryPage,
  AdminSectionTemplateEditPage,
  AdminApplicationSubmissionsPage,
} from '@/features/admin'
import { AdminMailingListPage } from '@/features/admin'
import { AdminDashboardPage } from '@/features/admin'
import { AdminSeasonsPage } from '@/features/admin'
import { AdminSeasonEditPage } from '@/features/admin'
import { AdminFilmsPage } from '@/features/admin'
import { AdminFilmEditPage } from '@/features/admin'
import { AdminProjectsPage } from '@/features/admin'
import { AdminUIShowcasePage } from '@/features/admin'
import { AdminEmailTemplatesPage } from '@/features/admin'
import { AdminConfigPage } from '@/features/admin'
import { AdminHomeHeroPage } from '@/features/admin'
import { AdminServicesPage } from '@/features/admin'
import { AdminFestivalsPage, AdminFestivalEditPage } from '@/features/admin'
import { AdminRequestsPage } from '@/features/admin'
import { ProjectsDashboardPage } from '@/features/admin'
import { ApplicationsDashboardPage } from '@/features/admin'
import {
  AdminProductionsPage,
  ProductionWizardPage,
  ProductionCommandCenterPage,
  CurationPage,
} from '@/features/admin/productions'
import { ProjectsBoardPage } from '@/features/admin'
import { PersonnelPage } from '@/features/admin'
import { ContractorInvoicesPage } from '@/features/admin'
import { TechInventoryPage } from '@/features/admin'
import { AdminUserLinksPage } from '@/features/admin'
import { AdminNotificationsPage } from '@/features/admin'
import { AdminMediaLibraryPage } from '@/features/admin'
import {
  AdminClientEngagementsPage,
  AdminClientsPage,
  AdminTermsOfServicePage,
  AdminTestimonialsPage,
  AdminPressKitPage,
} from '@/features/admin'
import { AdminStripeSettingsPage } from '@/features/admin'
import { SurveyPage } from '@/features/testimonials'
import { PressKitPage } from '@/features/press-kit'
import { EstimateAcceptPage } from '@/features/estimate'
import { ProviderPortalPage, ProviderInvoicesPage } from '@/features/providerPortal'
import { AdminProjectBoardPage, AdminBoardTemplatesPage } from '@/features/projectBoards'

// Shop
import {
  ShopPage,
  ProductDetailPage,
  CheckoutPage,
  OrderSuccessPage,
  AdminShopPage,
} from '@/features/shop'

// Dashboard
import { DashboardPage, DashboardProfilePage } from '@/features/dashboard'

// Settings
import { SettingsPage } from '@/features/settings'
import { ProtectedRoute } from '@/components/common/ProtectedRoute'
import { StaffRoute } from '@/components/common/StaffRoute'

// Ticketing
import {
  EventsPage,
  EventDetailPage,
  TicketSuccessPage,
  TicketLookupPage,
  TicketTransferClaimPage,
  MyTicketsPage,
  CheckInPage,
  AdminEventsPage,
  AdminEventEditPage,
  AdminEventTicketsPage,
  AdminStaffManagementPage,
} from '@/features/ticketing'

// Placeholder pages
import { NotFoundPage } from '@/pages/NotFoundPage'
import { RouteErrorPage } from '@/pages/RouteErrorPage'

export const router = createBrowserRouter([
  // ─── Authentication — dedicated full-screen experience ────────────────────
  { path: '/program/:slug/', element: <ProgramPage />, errorElement: <RouteErrorPage /> },
  { path: '/sign-in', element: <SignInPage />, errorElement: <RouteErrorPage /> },
  { path: '/register', element: <SignInPage />, errorElement: <RouteErrorPage /> },

  // ─── Public ────────────────────────────────────────────────────────────────
  {
    errorElement: <RouteErrorPage />,
    element: <PublicLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/seasons', element: <SeasonsPage /> },
      { path: '/films/projects', element: <ProjectsPage /> },
      { path: '/films/:slug', element: <FilmDetailPage /> },
      { path: '/company', element: <CompanyPage /> },
      { path: '/company/:slug', element: <MemberPage /> },
      { path: '/services', element: <ServicesPage /> },
      { path: '/request', element: <RequestPage /> },
      { path: '/applications', element: <ApplicationsPage /> },
      { path: '/applications/:slug', element: <ApplicationDetailPage /> },
      { path: '/productions/:slug/confirm', element: <ProductionConfirmPage /> },
      { path: '/festivals', element: <FestivalsPage /> },
      { path: '/festivals/:slug', element: <FestivalPage /> },
      { path: '/oddfest', element: <Navigate to="/festivals/oddfest" replace /> },
      { path: '/shift', element: <Navigate to="/festivals/shift" replace /> },
      { path: '/apply', element: <Navigate to="/applications" replace /> },
      { path: '/terms', element: <TermsOfServicePage /> },
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/press-kit', element: <PressKitPage /> },
      { path: '/connect', element: <ConnectPage /> },
      { path: '/estimate/:engagementId/accept/:token', element: <EstimateAcceptPage /> },
      { path: '/portal/requests', element: <ProviderPortalPage /> },
      { path: '/portal/invoices', element: <ProviderInvoicesPage /> },
      // Events & Ticketing
      { path: '/events', element: <EventsPage /> },
      { path: '/events/:slug', element: <EventDetailPage /> },
      { path: '/tickets/success', element: <TicketSuccessPage /> },
      { path: '/tickets/lookup', element: <TicketLookupPage /> },
      { path: '/tickets/transfer/claim', element: <TicketTransferClaimPage /> },
      { path: '/tickets/manage', element: <MyTicketsPage /> },
      // Shop
      { path: '/shop', element: <ShopPage /> },
      { path: '/shop/checkout', element: <CheckoutPage /> },
      { path: '/shop/order-success', element: <OrderSuccessPage /> },
      { path: '/shop/:productId', element: <ProductDetailPage /> },
      // 404 — catch-all within public layout
      { path: '*', element: <NotFoundPage /> },
      // Protected authenticated routes (still use PublicLayout)
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/settings', element: <Navigate to="/dashboard/settings" replace /> },
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/dashboard/profile', element: <DashboardProfilePage /> },
          { path: '/dashboard/settings', element: <SettingsPage /> },
          { path: '/survey', element: <SurveyPage /> },
          { path: '/my-tickets', element: <MyTicketsPage /> },
        ],
      },
    ],
  },

  // ─── Staff / Check-in ──────────────────────────────────────────────────────
  {
    errorElement: <RouteErrorPage />,
    element: <StaffRoute />,
    children: [
      {
        element: <PublicLayout />,
        children: [{ path: '/checkin', element: <CheckInPage /> }],
      },
    ],
  },

  // ─── Admin ─────────────────────────────────────────────────────────────────
  {
    errorElement: <RouteErrorPage />,
    element: <AdminRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          // Standard admin pages
          { path: '/admin', element: <AdminDashboardPage /> },
          { path: '/admin/mailing-list', element: <AdminMailingListPage /> },
          { path: '/admin/notifications', element: <AdminNotificationsPage /> },
          { path: '/admin/media', element: <AdminMediaLibraryPage /> },
          { path: '/admin/testimonials', element: <AdminTestimonialsPage /> },
          { path: '/admin/seasons/new', element: <AdminSeasonEditPage /> },
          { path: '/admin/seasons/:id/edit', element: <AdminSeasonEditPage /> },
          { path: '/admin/films/new', element: <AdminFilmEditPage /> },
          { path: '/admin/films/:id/edit', element: <AdminFilmEditPage /> },
          { path: '/admin/ui', element: <AdminUIShowcasePage /> },
          { path: '/admin/email-templates', element: <AdminEmailTemplatesPage /> },
          { path: '/admin/shop', element: <AdminShopPage /> },

          // Applications section
          { path: '/admin/applications/dashboard', element: <ApplicationsDashboardPage /> },
          { path: '/admin/applications', element: <AdminApplicationsPage /> },
          { path: '/admin/applications/new', element: <AdminApplicationEditPage /> },
          { path: '/admin/applications/:id/edit', element: <AdminApplicationEditPage /> },
          { path: '/admin/applications/:id/form', element: <AdminFormBuilderPage /> },
          {
            path: '/admin/applications/:id/submissions',
            element: <AdminApplicationSubmissionsPage />,
          },
          { path: '/admin/field-library', element: <AdminFieldLibraryPage /> },

          // Productions (Overture v1.1)
          { path: '/admin/productions', element: <AdminProductionsPage /> },
          { path: '/admin/productions/new', element: <ProductionWizardPage /> },
          { path: '/admin/productions/:id', element: <ProductionCommandCenterPage /> },
          { path: '/admin/productions/:id/program/:programId', element: <ProgramAdminPage /> },
          { path: '/admin/productions/:id/curate', element: <CurationPage /> },
          { path: '/admin/section-library', element: <AdminSectionLibraryPage /> },
          { path: '/admin/section-library/:id', element: <AdminSectionTemplateEditPage /> },

          // Events section
          { path: '/admin/events', element: <AdminEventsPage /> },
          { path: '/admin/events/new', element: <AdminEventEditPage /> },
          { path: '/admin/events/:id/edit', element: <AdminEventEditPage /> },
          { path: '/admin/events/:id/tickets', element: <AdminEventTicketsPage /> },

          // Config section
          {
            path: '/admin/config',
            children: [
              { index: true, element: <Navigate to="/admin/config/company" replace /> },
              { path: 'company', element: <AdminConfigPage /> },
              { path: 'home-hero', element: <AdminHomeHeroPage /> },
              { path: 'seasons', element: <AdminSeasonsPage /> },
              { path: 'films', element: <AdminFilmsPage /> },
              { path: 'projects', element: <AdminProjectsPage /> },
              { path: 'services', element: <AdminServicesPage /> },
              { path: 'board-templates', element: <AdminBoardTemplatesPage /> },
              { path: 'stripe', element: <AdminStripeSettingsPage /> },
              { path: 'festivals', element: <AdminFestivalsPage /> },
              { path: 'festivals/new', element: <AdminFestivalEditPage /> },
              { path: 'festivals/:id/edit', element: <AdminFestivalEditPage /> },
              { path: 'user-links', element: <AdminUserLinksPage /> },
              { path: 'terms', element: <AdminTermsOfServicePage /> },
              { path: 'press-kit', element: <AdminPressKitPage /> },
              { path: 'staff', element: <AdminStaffManagementPage /> },
            ],
          },

          // Client Services section
          {
            path: '/admin/client-services',
            children: [
              { index: true, element: <Navigate to="/admin/client-services/dashboard" replace /> },
              { path: 'dashboard', element: <ProjectsDashboardPage /> },
              { path: 'requests', element: <AdminRequestsPage /> },
              { path: 'board', element: <ProjectsBoardPage /> },
              { path: 'personnel', element: <PersonnelPage /> },
              { path: 'invoices', element: <ContractorInvoicesPage /> },
              { path: 'inventory', element: <TechInventoryPage /> },
              { path: 'engagements', element: <AdminClientEngagementsPage /> },
              { path: 'clients', element: <AdminClientsPage /> },
              { path: 'engagements/:engagementId/board', element: <AdminProjectBoardPage /> },
            ],
          },

          // Legacy redirect: /admin/projects/* → /admin/client-services/* (preserves subpath, search, hash)
          {
            path: '/admin/projects/*',
            element: <LegacyProjectsRedirect />,
          },
        ],
      },
    ],
  },
])
